import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import { Apple, Monitor, Smartphone, Tablet, Download, ArrowLeft, Check } from "lucide-react";

const BRAND = "#5A90F5";

// Downloads page — layout only for now. When installers ship, flip the
// disabled buttons on each card to real links.

const PLATFORMS = [
  {
    name: "macOS",
    desc: "Universal build for Apple Silicon and Intel Macs. 11+.",
    icon: Apple,
    color: "#EDEDED",
    requirement: "macOS 12 Monterey Or Later",
    filename: "Crescendo-Universal.dmg",
    badge: "Recommended",
  },
  {
    name: "Windows",
    desc: "Windows 10 and 11. MSI installer, auto-updates.",
    icon: Monitor,
    color: BRAND,
    requirement: "Windows 10 (64-bit) Or Later",
    filename: "CrescendoSetup.exe",
  },
  {
    name: "Linux",
    desc: "AppImage, .deb and .rpm packages.",
    icon: Monitor,
    color: "#FF8A00",
    requirement: "Ubuntu 22.04 · Fedora 38 · Or Compatible",
    filename: "Crescendo.AppImage",
  },
  {
    name: "iOS",
    desc: "Native iPhone + iPad companion with offline nodes.",
    icon: Smartphone,
    color: "#00C781",
    requirement: "iOS 17+",
    filename: null,
    teaser: "Via The App Store",
  },
  {
    name: "iPadOS",
    desc: "Full canvas with Apple Pencil sketching on edges.",
    icon: Tablet,
    color: "#A855F7",
    requirement: "iPadOS 17+",
    filename: null,
    teaser: "Via The App Store",
  },
  {
    name: "Android",
    desc: "Phone + tablet, background reminders, class sync.",
    icon: Smartphone,
    color: "#14B8A6",
    requirement: "Android 12+",
    filename: null,
    teaser: "Via Google Play",
  },
];

export default function Downloads() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] font-body">
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="downloads-logo">
            <Logo size={22} />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-[#EDEDED]" data-testid="downloads-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back To Site
          </Link>
        </div>
      </header>

      <section className="border-b border-[#1A1A1A] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-35" aria-hidden />
        <div className="absolute inset-x-0 -top-40 h-96 pointer-events-none" style={{
          background: `radial-gradient(55% 55% at 50% 50%, ${BRAND}22, transparent 70%)`,
        }} aria-hidden />
        <div className="relative max-w-4xl mx-auto px-8 py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#2A2A2A] bg-[#0F0F0F] text-[11px] uppercase tracking-wider text-[#A1A1AA] mb-6">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: BRAND }} />
            Installers Coming Soon
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tight text-[#EDEDED] leading-[0.95]">
            Put Crescendo <span className="italic" style={{ color: BRAND }}>everywhere</span> You Think.
          </h1>
          <p className="mt-6 text-base text-[#A1A1AA] max-w-2xl leading-relaxed">
            Native apps are on the way — same canvas, same data, offline-first. While we finish them, the web app is fully featured and lives at the same account you use here.
          </p>
        </div>
      </section>

      <section className="border-b border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-8 py-20">
          <div className="meta-label mb-3">PLATFORMS</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#EDEDED]">Pick Your Surface.</h2>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLATFORMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.name}
                  className="relative rounded-xl border border-[#1F1F1F] bg-[#0D0D0D] p-6 flex flex-col"
                  data-testid={`platform-card-${i}`}
                >
                  {p.badge && (
                    <div className="absolute -top-2.5 left-6 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider text-white font-semibold" style={{ background: BRAND }}>
                      {p.badge}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md border border-[#2A2A2A] flex items-center justify-center" style={{ background: `${p.color}14` }}>
                      <Icon className="w-5 h-5" style={{ color: p.color }} />
                    </div>
                    <div>
                      <div className="font-display text-xl text-[#EDEDED]">{p.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-[#A1A1AA]">{p.requirement}</div>
                    </div>
                  </div>

                  <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed flex-1">{p.desc}</p>

                  <button
                    type="button"
                    disabled
                    className="mt-6 inline-flex items-center justify-between gap-2 px-4 py-2.5 rounded-md border border-[#2A2A2A] bg-[#141414] text-sm text-[#A1A1AA] cursor-not-allowed"
                    data-testid={`platform-download-${p.name.toLowerCase()}`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Download className="w-3.5 h-3.5" />
                      {p.filename ? `Download — ${p.filename}` : (p.teaser || "Download")}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-[#555]">Coming Soon</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-[#1A1A1A]">
        <div className="max-w-5xl mx-auto px-8 py-20">
          <div className="meta-label mb-3">WHAT'S SHARED</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#EDEDED] max-w-3xl">
            One Account. One Canvas. Every Device.
          </h2>
          <ul className="mt-10 grid md:grid-cols-2 gap-x-10 gap-y-4">
            {[
              "Real-time sync of events, edges, notes, and classes",
              "Same Spotlight (⌘K) experience across all clients",
              "Version history restore from any device",
              "Workspaces, folders, and templates stay perfectly ordered",
              "Google sign-in + local accounts both work everywhere",
              "Offline-first on desktop — your canvas never blinks",
            ].map((l) => (
              <li key={l} className="flex items-start gap-2.5 text-sm text-[#EDEDED]">
                <Check className="w-4 h-4 text-[#00C781] mt-[2px] flex-shrink-0" />
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-8 py-10 flex items-center justify-between text-[11px] text-[#555]">
        <span>© {new Date().getFullYear()} Crescendo</span>
        <Link to="/" className="hover:text-[#A1A1AA]">Back To Site</Link>
      </div>
    </div>
  );
}
